import { BadgeType } from '../../types';

interface BadgeConfig {
  icon: React.ReactNode;
  accentColor: string;
  label: string;
  description: string;
}

// Checkered Flag Icon - F1 스타일
function CheckeredFlagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 4v16" strokeLinecap="round" />
      <path d="M4 4h16v8H4" fill="currentColor" fillOpacity="0.1" />
      <rect x="4" y="4" width="4" height="4" fill="currentColor" />
      <rect x="12" y="4" width="4" height="4" fill="currentColor" />
      <rect x="8" y="8" width="4" height="4" fill="currentColor" />
      <rect x="16" y="8" width="4" height="4" fill="currentColor" />
    </svg>
  );
}

const BADGE_CONFIG: Record<BadgeType, BadgeConfig> = {
  [BadgeType.BETA_TESTER]: {
    icon: <CheckeredFlagIcon />,
    accentColor: '#e10600',
    label: 'Beta Tester',
    description: 'Early supporter of Pelo 1',
  },
};

interface BadgeIconProps {
  type: BadgeType;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export function BadgeIcon({ type, size = 'sm', showLabel = false }: BadgeIconProps) {
  const config = BADGE_CONFIG[type];
  if (!config) return null;

  const sizeStyles = {
    sm: { container: 16, icon: 10, fontSize: 10, gap: 3 },
    md: { container: 20, icon: 12, fontSize: 11, gap: 4 },
    lg: { container: 24, icon: 14, fontSize: 12, gap: 5 },
  };

  const s = sizeStyles[size];

  return (
    <span
      title={config.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: s.gap,
      }}
    >
      <span
        style={{
          width: s.container,
          height: s.container,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${config.accentColor} 0%, #1a1a1a 100%)`,
          border: `1px solid ${config.accentColor}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ width: s.icon, height: s.icon, color: '#fff' }}>
          {config.icon}
        </span>
      </span>
      {showLabel && (
        <span
          style={{
            fontSize: s.fontSize,
            fontWeight: 600,
            color: config.accentColor,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {config.label}
        </span>
      )}
    </span>
  );
}

// Badge card for profile page - F1 minimal style
export function BadgeCard({
  type,
  awardedAt,
}: {
  type: BadgeType;
  awardedAt?: string;
}) {
  const config = BADGE_CONFIG[type];
  if (!config) return null;

  return (
    <div
      className="group relative p-4 rounded-lg transition-all duration-200"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${config.accentColor}40`;
        e.currentTarget.style.background = `linear-gradient(135deg, ${config.accentColor}08 0%, rgba(255,255,255,0.01) 100%)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)';
      }}
    >
      <div className="flex items-center gap-3">
        {/* Badge Icon */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 6,
            background: `linear-gradient(135deg, ${config.accentColor} 0%, #0a0a0a 100%)`,
            border: `1px solid ${config.accentColor}60`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ width: 20, height: 20, color: '#fff' }}>
            {config.icon}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: 0,
              }}
            >
              {config.label}
            </h3>
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: '2px 5px',
                borderRadius: 3,
                background: `${config.accentColor}20`,
                color: config.accentColor,
                border: `1px solid ${config.accentColor}40`,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              OG
            </span>
          </div>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
            {config.description}
          </p>
          {awardedAt && (
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', margin: '4px 0 0 0' }}>
              {new Date(awardedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Accent line */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 3,
          height: '60%',
          borderRadius: '0 2px 2px 0',
          background: config.accentColor,
        }}
      />
    </div>
  );
}

export function BadgeList({ badges, size = 'sm' }: { badges: BadgeType[]; size?: 'sm' | 'md' | 'lg' }) {
  if (!badges || badges.length === 0) return null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {badges.map((badge) => (
        <BadgeIcon key={badge} type={badge} size={size} />
      ))}
    </span>
  );
}
